import { IError, IResponseEvent } from "@core/data-access/model";
import { BusinessException } from "@core/error/business-exception";
import { CatchError } from "@core/error/catch-error";
import { IPageController } from ".";
import { IPagePathController } from "./path/page-path-controller";
import { envConfig } from "@env/env-config";
import { IMessageDialog } from "@qpUIs/qp-message-dialog/qp-message-dialog.interface";
import { IMessageDialogLevel, IUnhandleMessageDialog } from "@qpUIs/qp-message-dialog/qp-message-dialog-options.interface";
import { PromptTypeEnum } from "@modules/base/decorator/error/PromptType.enum";
import { IErrorConfig, ILogger } from "@core/logger/types";

export abstract class BasePageController implements IPageController, IPagePathController {

    /**
    * @description: 如果只是需要在function中使用参数 
    *    protected abstract rootPath 写法参照 AbstractAccessorService 中的 $accessor
    * @description: 如果需要在constructor中赋值参数
    *    constructor(public rootPath: string) 写法参照 PageObjectTreeService 中的 public storageService
    */
    protected abstract rootPath: string;

    protected $injector?: angular.auto.IInjectorService;

    protected supportmail: string = envConfig.supportEmail;


    constructor() { }

    /**
    * @description 检查用户页面访问权限
    */
    public abstract checkAuthorization(successFunc: Function, failureFunc?: Function): void;

    /**
     * @description Rest请求统一错误处理
     */
    public abstract onRequestFail<T extends IError>(e: IResponseEvent<T>);


    getInjectable(name: "$timeout"): angular.ITimeoutService;
    getInjectable(name: "$q"): angular.IQService;
    getInjectable(name: "$injector"): angular.auto.IInjectorService;
    getInjectable(name: "$location"): angular.ILocationService;
    getInjectable(name: "$throttle"): services.common.IThrottle;
    getInjectable(name: "$qpThrottle"): qpui.bootstrap.qpThrottle.IQpThrottleFactory;
    getInjectable(name: "qpMessageDialog"): IMessageDialog;
    getInjectable(name: "$translate"): angular.translate.ITranslateService;
    getInjectable(name: "$logger"): ILogger;
    /**
     * @deprecated 已弃用，请更换为$objectTree的形式
     */
    getInjectable(name: "runtimeContext"): qpmn.main.IRuntimeContext;
    getInjectable<T>(name: string): T;
    getInjectable<T>(name: string): T {
        if (!this.$injector) {
            throw new Error("missing $injector");
        }

        let injectable: T | undefined = undefined;
        if (this.$injector.has(name)) {
            injectable = this.$injector.get<T>(name);
        }

        if (!injectable) {
            throw new Error("missing injector: " + name);
        }

        return injectable;
    }

    public getUnhandleErrorMsgConfig(): IUnhandleMessageDialog {
        let $translate = this.getInjectable("$translate");

        let title: qpLanguage.keys = "UNEXPECTED_ERROR_TITLE";
        let key: qpLanguage.keys = "UNEXPECTED_ERROR_CONTENT";

        let msgConfigs = {
            title: $translate.instant(title),
            content: $translate.instant(key, { supportmail: this.supportmail }),
        }

        return msgConfigs;
    }

    public openUnhandleErrorMsgDialog(msgLevel: IMessageDialogLevel = "info"): void {
        let $translate = this.getInjectable("$translate");
        let $msgDialog = this.getInjectable("qpMessageDialog");

        let title: qpLanguage.keys = "UNEXPECTED_ERROR_TITLE";
        let key: qpLanguage.keys = "UNEXPECTED_ERROR_CONTENT";

        let msgConfigs = {
            title: $translate.instant(title),
            content: $translate.instant(key, { supportmail: this.supportmail }),
        }

        $msgDialog[msgLevel](msgConfigs);
    }

    /**
     * @description: 未处理业务请求错误及其他异常sentry手动上报
     * @param {IResponseEvent} e
     * @return {*}
     */
    public sendError(moduleName: string, e: IResponseEvent<IError> | string): void {
        if (this.$injector) {
            if (e == undefined || e == null) {
                return;
            }

            let $logger: ILogger | undefined;

            if (this.$injector.has("$logger")) {
                $logger = this.$injector.get<ILogger>("$logger");
            }

            if ($logger) {
                let err = new Error();

                err.name = moduleName;

                let obj: IErrorConfig = {
                    title: "请求接口错误--",
                    module: moduleName,
                    error: err
                };

                if (typeof e === "string") {
                    obj.title = "未知异常--" + e;
                } else if (typeof e === "object") {
                    let ctx;

                    if (e.config) {
                        ctx = {
                            url: e.config.url,
                            body: e.config.data,
                            params: e.config.params,
                            method: e.config.method,
                        }
                    } else {
                        ctx = e;
                    }

                    if (e.data) {
                        ctx.errCode = e.data?.data?.code;
                    }

                    obj.title = "请求接口错误--" + (e.data?.data?.message || "");
                    obj.context = ctx;
                } else {
                    obj.title = "未知异常--" + e;
                }

                $logger.error(obj);
            }
        }
    }

    /**
     * @description: 获取当前页面路径
     */
    public getCurrentPage(): string {
        let reg = /\/(\w+).html|\/([a-zA-Z0-9-]+).html/;

        let matched = reg.exec(window.location.pathname);

        if (matched && matched.length > 0) {
            return matched[1] || matched[2];
        } else {
            return "";
        }
    }

    /**
     * @description: 跳转至给定路径
     */
    public jumpTo(pagePath?: string | undefined): void {
        pagePath = pagePath || "";

        window.location.href = this.getInjectable("rootPath") + pagePath;
    }

    public $scope: angular.IScope;


    /**
     * @description
     * 由于一个页面可能由多个部分组成(如页头、页脚)，因此存在有多个Controller的情况
     * 因此在这里进行页面初始化事件广播，通知其他页面部分权限验证完成
     */
    protected authBroadcast() {

        // const rootScope = angular.element("$0").scope().$root;

        // console.log(rootScope);

        // rootScope.$broadcast("AUTH_FINISH");
        // if (this.$scope) {
        //     const rootScope = this.$scope.$root;

        // } else {
        //     const $injector = angular.injector();
        //     const rootScope = $injector.get('$rootScope');

        //     
        // }

    }

    /**
     * @description 抓取错误提示
     * 主要用于在嵌套函数中抓取报错信息
     * 若code在本地错误文件中能够匹配到对应的错误信息,则优先使用code匹配的错误信息,否则使用传入的message信息
     */
    // @CatchError()
    // public catchErrorFail(error: IError) {
    // if (angular.isUndefined(error.code)) {
    //     throw new BusinessException("A102B002");
    // }
    // if (error.message) {
    //     throw new BusinessException(error.code, error.message);
    // } else {
    //     throw new BusinessException(error.code);
    // }
    // }

    /**
     * @description 通用错误提示
     */
    public commonErrorHandle<T extends IError>(e: IResponseEvent<T>) {
        if (this.$injector) {
            let $translate: angular.translate.ITranslateService | undefined = undefined;
            let $msgDialog: IMessageDialog | undefined = undefined;

            if (this.$injector.has("$translate")) {
                $translate = this.$injector.get<angular.translate.ITranslateService>("$translate");
            }
            if (this.$injector.has("qpMessageDialog")) {
                $msgDialog = this.$injector.get<IMessageDialog>("qpMessageDialog");
            }

            if ($msgDialog && $translate) {
                if (e && e.data && e.data.data) {
                    let content = e.data.data.message;
                    const errorMsg = $translate.instant(e.data.data.code);
                    if (errorMsg && errorMsg != String(e.data.data.code)) {
                        content = errorMsg;
                    }

                    $msgDialog.info({
                        title: $translate.instant("UNEXPECTED_ERROR_TITLE"),
                        content: $translate.instant("UNEXPECTED_ERROR_CONTENT", { supportmail: this.supportmail }),
                        code: e.data.data.code,
                        msg: content
                    })
                } else if (typeof e === "string") {
                    $msgDialog.info({
                        title: $translate.instant("UNEXPECTED_ERROR_TITLE"),
                        content: $translate.instant("UNEXPECTED_ERROR_CONTENT", { supportmail: this.supportmail }),
                        code: e,
                        msg: e
                    })
                } else {
                    $msgDialog.info({
                        title: $translate.instant("UNEXPECTED_ERROR_TITLE"),
                        content: $translate.instant("UNEXPECTED_ERROR_CONTENT", { supportmail: this.supportmail })
                    })
                }
            } else if ($translate) {
                if (e && e.data && e.data.data) {
                    let content = e.data.data.message;
                    const errorMsg = $translate.instant(e.data.data.code);
                    if (errorMsg && errorMsg != String(e.data.data.code)) {
                        content = errorMsg;
                    }

                    window.qpAlertPrompt[PromptTypeEnum.INFO](
                        $translate.instant("UNEXPECTED_ERROR_TITLE"),
                        content || $translate.instant("UNEXPECTED_ERROR_CONTENT", { supportmail: this.supportmail })
                    )
                } else {
                    window.qpAlertPrompt[PromptTypeEnum.INFO](
                        $translate.instant("UNEXPECTED_ERROR_TITLE"),
                        $translate.instant("UNEXPECTED_ERROR_CONTENT", { supportmail: this.supportmail })
                    )
                }
            } else {
                window.qpAlertPrompt[PromptTypeEnum.INFO](
                    "Something Went Wrong",
                    `We encountered an issue. Please refresh the page and try again. If the problem persists, please contact our support team at ${this.supportmail}.`
                )
            }
        } else {
            window.qpAlertPrompt[PromptTypeEnum.INFO](
                "Something Went Wrong",
                `We encountered an issue. Please refresh the page and try again. If the problem persists, please contact our support team at ${this.supportmail}.`
            )
        }
    }
    
    public scrollViewToTop() {
        let html_collection = document.getElementsByTagName("html");

        let ele = html_collection[0];
    
        if (ele) {
            if (ele.scrollTop > 0) {
                ele.scrollTo({
                    top: 0,
                    behavior: "smooth"
                })
            }
        }
    }
}